/* ============================================================
   Projects section: category tabs + project modal.
   Self-contained (no theme JS dependency) so it is robust against
   async module load order. Handles: tab toggle, open on card click,
   close on X / overlay / Esc, focus trap, background scroll lock,
   return focus to the triggering card, and a swipe/arrow gallery.
   ============================================================ */
(function () {
	'use strict';

	var FOCUSABLE =
		'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

	function init(root) {
		if (!root || root.dataset.projectsInit === 'true') {
			return;
		}
		root.dataset.projectsInit = 'true';

		/* ---------------- Tabs ---------------- */
		var tabs = Array.prototype.slice.call(root.querySelectorAll('.projects__tab'));
		var panels = Array.prototype.slice.call(root.querySelectorAll('.projects__panel'));

		function activateTab(index) {
			tabs.forEach(function (tab) {
				var active = tab.dataset.index === index;
				tab.classList.toggle('is-active', active);
				tab.setAttribute('aria-selected', active ? 'true' : 'false');
			});
			panels.forEach(function (panel) {
				var match = panel.dataset.index === index;
				panel.classList.toggle('is-hidden', !match);
				if (match) {
					panel.removeAttribute('hidden');
				} else {
					panel.setAttribute('hidden', '');
				}
			});
		}

		tabs.forEach(function (tab) {
			tab.addEventListener('click', function () {
				activateTab(tab.dataset.index);
			});
		});

		/* ---------------- Modal ---------------- */
		var modal = root.querySelector('[data-projects-modal]');
		if (!modal) {
			return;
		}
		var dialog = modal.querySelector('.projects-modal__dialog');
		var content = modal.querySelector('[data-modal-content]');
		var overlay = modal.querySelector('[data-modal-overlay]');
		var closeBtn = modal.querySelector('[data-modal-close]');
		var lastTrigger = null;
		var scrollY = 0;
		var galleryTeardown = null;

		function visibleFocusable() {
			return Array.prototype.slice.call(dialog.querySelectorAll(FOCUSABLE)).filter(function (el) {
				return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
			});
		}

		function lockScroll() {
			scrollY = window.scrollY || window.pageYOffset || 0;
			document.body.style.position = 'fixed';
			document.body.style.top = '-' + scrollY + 'px';
			document.body.style.left = '0';
			document.body.style.right = '0';
			document.body.style.width = '100%';
		}

		function unlockScroll() {
			document.body.style.position = '';
			document.body.style.top = '';
			document.body.style.left = '';
			document.body.style.right = '';
			document.body.style.width = '';
			window.scrollTo(0, scrollY);
		}

		function onKeydown(event) {
			if (event.key === 'Escape' || event.key === 'Esc') {
				event.preventDefault();
				closeModal();
				return;
			}
			if (event.key !== 'Tab') {
				return;
			}
			var items = visibleFocusable();
			if (!items.length) {
				event.preventDefault();
				dialog.focus();
				return;
			}
			var first = items[0];
			var last = items[items.length - 1];
			if (event.shiftKey && document.activeElement === first) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault();
				first.focus();
			}
		}

		// Transform-based carousel. Desktop/tablet shows 2 images with a sliding
		// window ([1,2],[2,3],…); mobile shows 1. Infinite wrap on arrows, dots
		// and swipe. Returns a teardown to drop the window/touch listeners.
		function setupGallery() {
			var gallery = dialog.querySelector('[data-gallery]');
			if (!gallery) {
				return null;
			}
			var track = gallery.querySelector('[data-gallery-track]');
			var prev = gallery.querySelector('[data-gallery-prev]');
			var next = gallery.querySelector('[data-gallery-next]');
			var dotsWrap = dialog.querySelector('[data-gallery-dots]');
			if (!track) {
				return null;
			}

			var realSlides = Array.prototype.slice.call(track.querySelectorAll('.projects-modal__slide'));
			var realCount = realSlides.length;
			var index = 0;

			function isDesktop() {
				return window.matchMedia('(min-width: 768px)').matches;
			}

			function visibleCount() {
				return isDesktop() ? 2 : 1;
			}

			// One slide position per image start. With more images than are visible the
			// window wraps (3 images desktop => [1,2], [2,3], [3,1]); with exactly
			// `visible` (or fewer) images it's a single static slide.
			function positions() {
				return realCount > visibleCount() ? realCount : 1;
			}

			// Pixel distance between consecutive slide starts (image width + gap),
			// measured from the real slides (clones are appended after them).
			function step() {
				return realCount > 1 ? realSlides[1].offsetLeft - realSlides[0].offsetLeft : 0;
			}

			// For the wrapping window, append clones of the first (visible - 1) images
			// so the final slide can show the wrap pair (e.g. [3,1]). Rebuilt per viewport.
			function syncClones() {
				Array.prototype.slice.call(track.querySelectorAll('[data-clone]')).forEach(function (c) {
					track.removeChild(c);
				});
				if (realCount > visibleCount()) {
					for (var i = 0; i < visibleCount() - 1; i++) {
						var clone = realSlides[i].cloneNode(true);
						clone.setAttribute('data-clone', '');
						clone.setAttribute('aria-hidden', 'true');
						track.appendChild(clone);
					}
				}
			}

			function render(animate) {
				track.style.transition = animate ? 'transform 0.3s ease' : 'none';
				track.style.transform = 'translateX(' + (-index * step()) + 'px)';
				updateDots();
			}

			function goTo(i, animate) {
				var total = positions();
				index = (i % total + total) % total; // infinite wrap, no dead ends
				render(animate);
			}

			function buildDots() {
				if (!dotsWrap) {
					return;
				}
				dotsWrap.innerHTML = '';
				var total = positions();
				if (total <= 1) {
					dotsWrap.style.display = 'none';
					return;
				}
				dotsWrap.style.display = '';
				for (var i = 0; i < total; i++) {
					var dot = document.createElement('button');
					dot.type = 'button';
					dot.className = 'projects-modal__dot';
					dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
					(function (idx) {
						dot.addEventListener('click', function () { goTo(idx, true); });
					})(i);
					dotsWrap.appendChild(dot);
				}
			}

			function updateDots() {
				if (!dotsWrap) {
					return;
				}
				var dots = dotsWrap.children;
				for (var i = 0; i < dots.length; i++) {
					var active = i === index;
					dots[i].classList.toggle('is-active', active);
					dots[i].setAttribute('aria-current', active ? 'true' : 'false');
				}
			}

			function updateArrows() {
				// Hidden for single image and for the desktop exact-2 static slide.
				// On mobile the CSS keeps arrows hidden regardless ('' falls back to CSS).
				var hide = positions() <= 1;
				[prev, next].forEach(function (btn) {
					if (btn) { btn.style.display = hide ? 'none' : ''; }
				});
			}

			if (prev) {
				prev.addEventListener('click', function () { goTo(index - 1, true); });
			}
			if (next) {
				next.addEventListener('click', function () { goTo(index + 1, true); });
			}

			// ----- Axis-aware touch swipe (claims horizontal, lets vertical scroll pass) -----
			var startX = 0, startY = 0, dragging = false, axis = null;
			function onTouchStart(e) {
				var t = e.touches[0];
				startX = t.clientX;
				startY = t.clientY;
				dragging = true;
				axis = null;
			}
			function onTouchMove(e) {
				if (!dragging) { return; }
				var t = e.touches[0];
				var dx = t.clientX - startX;
				var dy = t.clientY - startY;
				if (axis === null && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
					axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
				}
				if (axis === 'x') { e.preventDefault(); }
			}
			function onTouchEnd(e) {
				if (!dragging) { return; }
				dragging = false;
				if (axis !== 'x') { return; }
				var dx = e.changedTouches[0].clientX - startX;
				if (dx <= -40) { goTo(index + 1, true); }
				else if (dx >= 40) { goTo(index - 1, true); }
			}
			track.addEventListener('touchstart', onTouchStart, { passive: true });
			track.addEventListener('touchmove', onTouchMove, { passive: false });
			track.addEventListener('touchend', onTouchEnd);

			function layout() {
				syncClones();
				var max = positions() - 1;
				if (index > max) { index = max; }
				if (index < 0) { index = 0; }
				buildDots();
				updateArrows();
				render(false);
			}

			var resizeTimer = null;
			function onResize() {
				clearTimeout(resizeTimer);
				resizeTimer = setTimeout(layout, 150);
			}
			window.addEventListener('resize', onResize);

			layout();

			return function teardown() {
				window.removeEventListener('resize', onResize);
				clearTimeout(resizeTimer);
				track.removeEventListener('touchstart', onTouchStart);
				track.removeEventListener('touchmove', onTouchMove);
				track.removeEventListener('touchend', onTouchEnd);
			};
		}

		function openModal(trigger) {
			var key = trigger.getAttribute('data-project');
			if (!key) {
				return;
			}
			var selectorKey = window.CSS && CSS.escape ? CSS.escape(key) : key;
			var tpl = root.querySelector('template[data-project-template="' + selectorKey + '"]');
			if (!tpl) {
				return;
			}

			lastTrigger = trigger;
			content.innerHTML = '';
			content.appendChild(tpl.content.cloneNode(true));

			var title = content.querySelector('.projects-modal__title');
			if (title) {
				dialog.setAttribute('aria-label', title.textContent.trim());
			}

			modal.classList.add('is-open');
			modal.setAttribute('aria-hidden', 'false');
			lockScroll();
			galleryTeardown = setupGallery();
			document.addEventListener('keydown', onKeydown);

			var items = visibleFocusable();
			(items[0] || dialog).focus();
		}

		function closeModal() {
			if (!modal.classList.contains('is-open')) {
				return;
			}
			modal.classList.remove('is-open');
			modal.setAttribute('aria-hidden', 'true');
			document.removeEventListener('keydown', onKeydown);
			unlockScroll();
			if (galleryTeardown) {
				galleryTeardown();
				galleryTeardown = null;
			}
			content.innerHTML = '';
			if (lastTrigger && document.body.contains(lastTrigger)) {
				lastTrigger.focus();
			}
			lastTrigger = null;
		}

		root.querySelectorAll('[data-project]').forEach(function (card) {
			card.addEventListener('click', function () {
				openModal(card);
			});
		});

		if (closeBtn) {
			closeBtn.addEventListener('click', closeModal);
		}

		// Close on overlay / outside-dialog click only (not on dialog content).
		modal.addEventListener('click', function (event) {
			if (event.target === overlay || event.target === modal) {
				closeModal();
			}
		});
	}

	function initAll(scope) {
		var container = scope || document;
		Array.prototype.slice.call(container.querySelectorAll('[data-projects-section]')).forEach(init);
	}

	if (document.readyState !== 'loading') {
		initAll();
	} else {
		document.addEventListener('DOMContentLoaded', function () {
			initAll();
		});
	}

	// Shopify theme editor: re-init when the section is reloaded.
	document.addEventListener('shopify:section:load', function (event) {
		initAll(event.target);
	});
})();
